<script setup lang="ts">
import { Page, Section } from "@/components/Ui";
import { ReviewPlatform, ReviewsFavorite } from "@/components/Reviews";
import {
  BaseAffiliate
} from "@/components/Sections";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

const route = useRoute();
const { locale, t } = useI18n();
const localePath = useLocalePath();
const paramsPlatforms = {
  fields: ["color"]
};
const itemsParams = {
  sort: "date",
  "filters[favorite][$eq]": true,
  fields: ["rating"]
};
const [{ pending: pendingPlatform, data: platformsData }, {
  data: itemsData
}] = await Promise.all([
  useLazyFetch("/api/review-platforms", { params: paramsPlatforms }),
  useFetch("/api/review-items", { params: itemsParams })
]);
const platforms = computed<any>(() => {
  return platformsData.value?.data;
});
useSeoMeta({
  title: t("page.reviews.seo.title"),
  description: t("page.reviews.seo.description"),
  ogTitle: t("page.reviews.seo.title"),
  ogDescription: t("page.reviews.seo.description")
});
</script>

<template>
  <Page :class="$style.root">
    <div :class="$style.head" class="container">
      <h1 :class="[$style.title, $style[`title-${$i18n.locale}`]]">
        {{ $t("reviews.title") }}
      </h1>
      <p>
        {{ $t("reviews.subtitle") }}
      </p>
    </div>
    <Section full-width :class="$style.favorite">
      <ReviewsFavorite
        :items="itemsData?.data"
      />
    </Section>
    <Section>
      <h2>{{ $t("reviews.readMore") }}</h2>
      <ul v-if="!pendingPlatform" :class="`${$style.nav} ${$style[`template-${platforms?.length}`]}`">
        <ReviewPlatform
          v-for="(item, index) in platforms"
          :key="index"
          :itemId="item.id"
          :color="item.attributes.color"
        />
      </ul>
    </Section>
    <BaseAffiliate />
  </Page>
</template>

<style lang="scss" module>
.root {

  @apply mt-[70px] md:mt-[90px];

  & h2 {
    @apply text-neutral-800 text-center text-[36px] font-black leading-none;
  }
}

.head {
  @apply pt-8 pb-[80px] flex flex-col gap-6;

  & p {
    @apply text-neutral-800 text-center text-xl xxs:text-2xl;
  }
}
.title {
  @apply text-center font-black leading-none;
  @apply text-[48px] xxs:text-[52px] md:text-[58px] lg:text-[60px];
  &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
  &-be,
  &-it,
  &-ua,
  &-es,
  &-fr,
  &-ja,
  &-bg,
  &-ru,
  &-zh,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw{
    @apply text-[48px] xxs:text-[52px] md:text-[58px] lg:text-[60px];
  }
  &-tr {
    @apply text-[42px] xxs:text-[48px] md:text-[58px] lg:text-[60px];
  }
  &-pt,
  &-de {
    @apply text-[42px] xxs:text-[52px] md:text-[58px] lg:text-[60px];
  }
  &-zh,
  &-ja {
    @apply leading-[1.2];
  }
}
.favorite {
  @apply mb-[80px];
}

.nav {
  @apply w-full mb-[80px] grid gap-5;
}

.template {
  &-1 {
    @apply grid-cols-1;
    &-li {
      @apply col-span-1;
    }
  }

  &-2 {
    @apply grid-cols-1 md:grid-cols-2;
    &-li {
      @apply col-span-1;
    }
  }

  &-3 {
    @apply grid-cols-1 md:grid-cols-2;
    & li {
      &:first-of-type {
        @apply col-span-2;
      }
    }
  }

  &-5 {
    @apply grid-cols-1 md:grid-cols-6;
    & li {
      &:nth-child(-n+2) {
        @apply col-span-1 md:col-span-3;
      }

      &:nth-child(n+3) {
        @apply col-span-1 md:col-span-2;
      }
    }
  }

  &-6, &-9, &-12 {
    @apply grid-cols-1 md:grid-cols-3;
    & li {
      @apply col-span-1;
    }
  }

  &-7, &-10 {
    @apply grid-cols-1 md:grid-cols-6;
    & li {
      &:nth-child(-n+4) {
        @apply col-span-1 md:col-span-3;
      }

      &:nth-child(n+5) {
        @apply col-span-1 md:col-span-2;
      }
    }
  }
  &-8, &-11 {
    @apply grid-cols-1 md:grid-cols-6;
    & li {
      &:nth-child(-n+2) {
        @apply col-span-1 md:col-span-3;
      }

      &:nth-child(n+3) {
        @apply col-span-1 md:col-span-2;
      }
    }
  }
}
</style>
